import { useState } from 'react';
import { useFormErrorHandler } from './useFormErrorHandler';

/**
 * Common form state and logic hook
 * Provides standardized form handling across all forms
 */
export const useCommonForm = ({
    initialData = {},
    onSubmit,
    externalLoading = false,
    validationRules = {}
}) => {
    const [formData, setFormData] = useState(initialData);
    const [internalLoading, setInternalLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const errorHandler = useFormErrorHandler();

    // Combined loading state
    const loading = externalLoading || internalLoading || isSubmitting;

    /**
     * Handle form field changes
     * @param {Event|Object} e - Event object or { name, value, type, checked }
     */
    const handleChange = (e) => {
        let name, value, type, checked;

        if (e.target) {
            ({ name, value, type, checked } = e.target);
        } else {
            ({ name, value, type, checked } = e);
        }

        // Clear field error when user starts typing
        if (errorHandler.getFieldError(name)) {
            errorHandler.clearFieldError(name);
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    /**
     * Update form data programmatically
     * @param {Object} newData - Data to merge with current form data
     */
    const updateFormData = (newData) => {
        setFormData(prev => ({
            ...prev,
            ...newData
        }));
    };

    /**
     * Reset form to initial state
     * @param {Object} data - Optional new initial data
     */
    const resetForm = (data = initialData) => {
        setFormData(data);
        errorHandler.clearErrors();
    };

    /**
     * Validate form based on provided rules
     * @returns {boolean} - True if valid, false otherwise
     */
    const validateForm = () => {
        const errors = {};
        let isValid = true;

        Object.entries(validationRules).forEach(([field, rules]) => {
            const value = formData[field];

            // Required field validation
            if (rules.required && (!value || (typeof value === 'string' && !value.trim()))) {
                errors[field] = rules.requiredMessage || `${field} is required`;
                isValid = false;
            }

            // Minimum length validation
            if (rules.minLength && value && value.length < rules.minLength) {
                errors[field] = rules.minLengthMessage || `${field} must be at least ${rules.minLength} characters`;
                isValid = false;
            }

            // Maximum length validation
            if (rules.maxLength && value && value.length > rules.maxLength) {
                errors[field] = rules.maxLengthMessage || `${field} must be no more than ${rules.maxLength} characters`;
                isValid = false;
            }

            // Pattern validation
            if (rules.pattern && value && !rules.pattern.test(value)) {
                errors[field] = rules.patternMessage || `${field} format is invalid`;
                isValid = false;
            }

            // Custom validation function
            if (rules.custom && typeof rules.custom === 'function') {
                const customError = rules.custom(value, formData);
                if (customError) {
                    errors[field] = customError;
                    isValid = false;
                }
            }
        });

        // Set field errors if any
        if (!isValid) {
            Object.entries(errors).forEach(([field, message]) => {
                errorHandler.setFieldError(field, message);
            });
        }

        return isValid;
    };

    /**
     * Handle form submission
     * @param {Event} e - Form submission event
     */
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Clear previous errors
        errorHandler.clearErrors();

        // Validate form if rules are provided
        if (Object.keys(validationRules).length > 0 && !validateForm()) {
            return;
        }

        if (externalLoading) {
            // If external loading is managed, just call onSubmit
            try {
                await onSubmit(formData);
            } catch (err) {
                errorHandler.handleError(err, { rethrow: true });
            }
        } else {
            // Manage loading internally
            setInternalLoading(true);
            setIsSubmitting(true);

            try {
                await onSubmit(formData);
            } catch (err) {
                errorHandler.handleError(err);
            } finally {
                setInternalLoading(false);
                setIsSubmitting(false);
            }
        }
    };

    /**
     * Get field props for form inputs
     * @param {string} fieldName - Name of the field
     * @param {Object} extraProps - Additional props to merge
     * @returns {Object} - Props object for the input
     */
    const getFieldProps = (fieldName, extraProps = {}) => {
        return {
            name: fieldName,
            value: formData[fieldName] || '',
            onChange: handleChange,
            disabled: loading,
            className: errorHandler.getFieldError(fieldName) ? 'error' : '',
            ...extraProps
        };
    };

    /**
     * Get checkbox field props
     * @param {string} fieldName - Name of the field
     * @param {Object} extraProps - Additional props to merge
     * @returns {Object} - Props object for the checkbox input
     */
    const getCheckboxProps = (fieldName, extraProps = {}) => {
        return {
            name: fieldName,
            checked: !!formData[fieldName],
            onChange: handleChange,
            disabled: loading,
            type: 'checkbox',
            ...extraProps
        };
    };

    return {
        // Form state
        formData,
        loading,
        isSubmitting,

        // Error handling
        error: errorHandler.error,
        fieldErrors: errorHandler.fieldErrors,
        getFieldError: errorHandler.getFieldError,
        hasErrors: errorHandler.hasErrors,
        clearErrors: errorHandler.clearErrors,
        clearFieldError: errorHandler.clearFieldError,
        setCustomError: errorHandler.setCustomError,
        handleError: errorHandler.handleError,

        // Form methods
        handleChange,
        handleSubmit,
        updateFormData,
        resetForm,
        validateForm,
        getFieldProps,
        getCheckboxProps
    };
};

export default useCommonForm;
